"use client";

import Image from "next/image";
import { useEffect, useState } from "react";
import type { Locale, Review } from "@/lib/api";
import { t } from "@/lib/i18n";
import "../styles/legacy-home-shared.css";

const M = "ReviewsSection-module__l1BLpG__";
const A = "HomePage-module__TYHVLa__";
const REVIEW_PREVIEW_WORDS = 16;

function Stars({ rating }: { rating: number }) {
  const full = Math.round(rating);
  return (
    <div className={`${M}stars`} role="img" aria-label={`Rating ${full} of 5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <span
          key={i}
          aria-hidden="true"
          className={`${M}starGlyph`}
          style={{ opacity: i < full ? 1 : 0.3 }}
        >
          ★
        </span>
      ))}
    </div>
  );
}

function getInitials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function getReviewPreview(text: string) {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length <= REVIEW_PREVIEW_WORDS) {
    return { text: text.trim(), isTruncated: false };
  }
  return {
    text: words.slice(0, REVIEW_PREVIEW_WORDS).join(" "),
    isTruncated: true,
  };
}

function ReviewAvatar({
  src,
  name,
}: {
  src: string | null;
  name: string;
}) {
  const [failed, setFailed] = useState(!src);

  if (!src || failed) {
    return (
      <span className={`${M}avatar ${M}avatarFallback`} role="img" aria-label={name}>
        {getInitials(name) || "IC"}
      </span>
    );
  }

  if (src.startsWith("/_next/image?")) {
    return (
      <img
        src={src}
        alt={name}
        width={34}
        height={34}
        className={`${M}avatar`}
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <Image
      src={src}
      alt={name}
      width={34}
      height={34}
      className={`${M}avatar`}
      sizes="34px"
      onError={() => setFailed(true)}
    />
  );
}

export function ReviewsSection({
  title = "What our clients say",
  allLink,
  allLinkHref = "https://maps.app.goo.gl/BK4bZd7VGJWzFyTt8?g_st=ic",
  reviews,
  locale = "en",
}: {
  title?: string;
  allLink?: string;
  allLinkHref?: string;
  reviews: Review[];
  locale?: Locale;
}) {
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [showAll, setShowAll] = useState(false);
  const s = t(locale);
  const allReviewsLabel = allLink ?? s.reviewsAll;
  const moreLabel = locale === "ru" ? "подробнее" : "read more";
  const closeLabel = locale === "ru" ? "Закрыть отзыв" : "Close review";

  useEffect(() => {
    if (!selectedReview) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSelectedReview(null);
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [selectedReview]);

  return (
    <div id="home-reviews" className={`${A}anchor`} data-home-section="reviews">
      <section className={`${M}section`}>
        <div className={`${M}wrap`}>
          <div className={`${M}head`}>
            <h2 className={`${M}h2`}>{title}</h2>
            <div className={`${M}headRight`}>
              <a
                className={`${M}allLink`}
                href={allLinkHref}
                aria-label={`${allReviewsLabel} on Google Maps`}
                target="_blank"
                rel="noreferrer"
              >
                {allReviewsLabel}
              </a>
            </div>
          </div>
          <div className={`${M}grid`}>
            {reviews.map((r, i) => {
              const preview = getReviewPreview(r.text);
              const authorName = r.author_display_name || "iSellCars customer";

              return (
                <div
                  key={i}
                  className={`${M}card${i >= 3 && !showAll ? " home-review-hidden" : ""}`}
                  data-review-card={i + 1}
                >
                  <div className={`${M}headAuthor`}>
                    <ReviewAvatar
                      src={r.author_photo_url}
                      name={authorName}
                    />
                    {r.author_uri ? (
                      <a
                        className={`${M}name`}
                        href={r.author_uri}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {authorName}
                      </a>
                    ) : (
                      <span className={`${M}name`}>{authorName}</span>
                    )}
                  </div>
                  <div className={`${M}text`}>
                    {preview.text}
                    {preview.isTruncated ? (
                      <>
                        {"... "}
                        <button
                          type="button"
                          className={`${M}moreButton`}
                          onClick={() => setSelectedReview(r)}
                        >
                          {moreLabel}
                        </button>
                      </>
                    ) : null}
                  </div>
                  <div className={`${M}bottom`}>
                    <Stars rating={r.rating} />
                    <div className={`${M}source`}>
                      {s.reviewsSource}: Google Maps
                      {r.relative_publish_time_description ? ` · ${r.relative_publish_time_description}` : ""}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {reviews.length > 3 ? (
            <button
              className="home-reviews-toggle"
              type="button"
              aria-expanded={showAll}
              onClick={() => setShowAll((value) => !value)}
            >
              {showAll
                ? locale === "ru" ? "Скрыть" : "Show less"
                : locale === "ru" ? "Показать ещё" : "Show more"}
            </button>
          ) : null}
        </div>
      </section>
      {selectedReview ? (
        <div
          className={`${M}modalBackdrop`}
          role="presentation"
          onClick={() => setSelectedReview(null)}
        >
          <div
            className={`${M}modal`}
            role="dialog"
            aria-modal="true"
            aria-label={selectedReview.author_display_name}
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className={`${M}modalClose`}
              aria-label={closeLabel}
              onClick={() => setSelectedReview(null)}
            >
              ×
            </button>
            <div className={`${M}headAuthor ${M}modalAuthor`}>
              <ReviewAvatar
                src={selectedReview.author_photo_url}
                name={selectedReview.author_display_name || "iSellCars customer"}
              />
              {selectedReview.author_uri ? (
                <a
                  className={`${M}name`}
                  href={selectedReview.author_uri}
                  target="_blank"
                  rel="noreferrer"
                >
                  {selectedReview.author_display_name || "iSellCars customer"}
                </a>
              ) : (
                <span className={`${M}name`}>
                  {selectedReview.author_display_name || "iSellCars customer"}
                </span>
              )}
            </div>
            <div className={`${M}modalText`}>{selectedReview.text}</div>
            <div className={`${M}bottom`}>
              <Stars rating={selectedReview.rating} />
              <div className={`${M}source`}>
                {s.reviewsSource}: Google Maps
                {selectedReview.relative_publish_time_description
                  ? ` · ${selectedReview.relative_publish_time_description}`
                  : ""}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
